import React, { useCallback, useEffect, useState } from 'react'
import { Box, CircularProgress, IconButton, Skeleton, Tooltip, Zoom } from '@mui/material'
import { ThumbUp } from '@mui/icons-material'
import { useStyles } from './style'
import { useDispatch, useSelector } from 'react-redux'
import { contentSlice } from '../../../reducers'
import { useAlert } from '../../../hooks'
import { useLike } from '../../like'
import { useSession } from '@emerald-works/react-auth'
import useFreeAccountCta from '../../../hooks/useFreeAccountCta'

const {
  actions: { recommendToggle }
} = contentSlice

const COMPONENT_STATE = {
  BASE: 'base',
  LOADING: 'loading',
  THANK_YOU_MESSAGE: 'thank_you_message'
}

const ToolbarRecommend = props => {
  const classes = useStyles(props)
  const [componentState, setComponentState] = useState(COMPONENT_STATE.BASE)
  const { locationId, hasRecommend, recommendCount } = useSelector(contentSlice.selectors.selectContent)
  const dispatch = useDispatch()
  const { enqueueMsg } = useAlert()
  const { isAuthenticated } = useSession()
  const { openFreeAccountModalForLike } = useFreeAccountCta()
  const isLoadingExtraInfo = useSelector(contentSlice.selectors.selectIsLoadingExtraInfo)

  const onStart = useCallback(() => {
    setComponentState(COMPONENT_STATE.LOADING)
  }, [])

  const onSuccess = useCallback((payload) => {
    const { hasRecommend } = payload
    dispatch(recommendToggle(payload))

    const newComponentState = hasRecommend ? COMPONENT_STATE.THANK_YOU_MESSAGE : COMPONENT_STATE.BASE
    setComponentState(newComponentState)
  }, [dispatch])

  const onError = useCallback(() => {
    setComponentState(COMPONENT_STATE.BASE)
    enqueueMsg('Unable to toggle like', 'error')
  }, [enqueueMsg])

  const { handleLike } = useLike({ onSuccess, onError, onStart, from: 'article' })

  useEffect(() => {
    if (componentState === COMPONENT_STATE.THANK_YOU_MESSAGE) {
      // We should hide the thanks message after 3s
      setTimeout(() => setComponentState(COMPONENT_STATE.BASE), 3000)
    }
  }, [componentState])

  const handleRecommend = useCallback((ev) => {
    if (!isAuthenticated) {
      return openFreeAccountModalForLike()
    }
    handleLike({ locationId, hasRecommend })
  }, [locationId, handleLike, hasRecommend, openFreeAccountModalForLike, isAuthenticated])

  if (componentState === COMPONENT_STATE.LOADING) {
    return (
      <Box className={classes.circularProgressContainer}>
        <CircularProgress size='1.5rem' />
      </Box>
    )
  }

  if (componentState === COMPONENT_STATE.THANK_YOU_MESSAGE) {
    return (
      <Zoom in>
        <Box className={classes.thankYouContainer}>
          <span>Thank you</span>
        </Box>
      </Zoom>
    )
  }

  return (
    <Tooltip
      title='Like this article'
      PopperProps={{ 'data-test': 'article-like-tooltip' }}
    >
      <Zoom in>
        <div>
          <Box className={classes.likeIconContainer}>
            <IconButton disabled={isLoadingExtraInfo} onClick={handleRecommend} data-test='article-like' size='large'>
              {hasRecommend ? <ThumbUp color='primary' /> : <ThumbUp />}
            </IconButton>
            {isLoadingExtraInfo
              ? <span style={{ display: 'flex', flexDirection: 'row' }}>(<Skeleton width={20} />)</span>
              : <span>({Math.max(recommendCount, 0)})</span>}
          </Box>
        </div>
      </Zoom>
    </Tooltip>
  )
}

export default ToolbarRecommend
